/**
 * Diff utility functions for FamiliesPage
 * Used for parsing and matching diff data in change history
 */

/**
 * Get the log/type category from an activity string
 * @param {string} activity - Activity description (e.g., 'Ruleset Saved', 'Feature Created')
 * @returns {string} Type category: 'RuleSet', 'Feature', 'ProductFamily', or empty string
 */
export function getLogType(activity) {
    if (activity.includes('Ruleset')) return 'RuleSet';
    if (activity.includes('Feature')) return 'Feature';
    if (activity.includes('Family')) return 'ProductFamily';
    return '';
}

/**
 * Parse a row string from diff data into an object
 * Expected format: "key1: value1, key2: value2, ..."
 * @param {string} rowStr - Row string to parse
 * @returns {Object} Parsed key-value object
 */
export function parseRow(rowStr) {
    const cells = {};
    if (!rowStr) return cells;
    
    const parts = rowStr.split(', ');
    parts.forEach(part => {
        const colonIdx = part.indexOf(': ');
        if (colonIdx !== -1) {
            const varName = part.substring(0, colonIdx);
            const value = part.substring(colonIdx + 2);
            cells[varName] = value;
        }
    });
    return cells;
}

/**
 * Extract the category/key from a diff text line
 * Removes prefixes like '+ ' or '- ' and extracts the part before ':'
 * @param {string} text - Diff text line (e.g., '+ Name: OldName')
 * @returns {string} Category/key extracted from the line
 */
export function getCategory(text) {
    if (!text) return '';
    
    let clean = text;
    // Remove + or - prefix if present
    if (text.startsWith('+ ') || text.startsWith('- ')) {
        clean = text.substring(2);
    }
    
    const colonIdx = clean.indexOf(':');
    if (colonIdx !== -1) {
        return clean.substring(0, colonIdx).trim();
    }
    
    return clean.trim();
}

/**
 * Calculate similarity score between two rows (objects)
 * Used to match deleted rows with added rows in diff analysis
 * @param {Object} rowA - First row object
 * @param {Object} rowB - Second row object
 * @returns {number} Number of matching key-value pairs
 */
export function getSimilarityScore(rowA, rowB) {
    let score = 0;
    const keys = Object.keys(rowA);
    
    keys.forEach(k => {
        if (rowA[k] !== undefined && rowA[k] === rowB[k]) {
            score++;
        }
    });
    
    return score;
}

/**
 * Match deleted rows with added rows based on similarity
 * @param {Array} deletedRows - Array of deleted row objects
 * @param {Array} addedRows - Array of added row objects
 * @returns {Object} Mapping of deleted row indices to added row indices
 */
export function matchRows(deletedRows, addedRows) {
    const matchedAddedIndices = new Set();
    const deleteToAddMatch = {};

    for (let dIdx = 0; dIdx < deletedRows.length; dIdx++) {
        let bestScore = -1;
        let bestAIdx = -1;
        
        for (let aIdx = 0; aIdx < addedRows.length; aIdx++) {
            if (matchedAddedIndices.has(aIdx)) continue;
            
            const score = getSimilarityScore(deletedRows[dIdx], addedRows[aIdx]);
            
            if (score > bestScore) {
                bestScore = score;
                bestAIdx = aIdx;
            }
        }
        
        // Match if there's a good score, or if there's only one of each (likely the same row)
        if (bestAIdx !== -1 && (bestScore > 0 || (deletedRows.length === 1 && addedRows.length === 1))) {
            deleteToAddMatch[dIdx] = bestAIdx;
            matchedAddedIndices.add(bestAIdx);
        }
    }
    
    return deleteToAddMatch;
}

/**
 * Parse multiple rows from a semicolon-separated string
 * @param {string} rowsStr - String containing rows separated by '; '
 * @returns {Array} Array of parsed row objects
 */
export function parseRows(rowsStr) {
    if (!rowsStr) return [];
    return rowsStr.split('; ').map(parseRow).filter(row => Object.keys(row).length > 0);
}
